import { ArrowRight, Gem, Shield, Award, Sparkles } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import PropertyContact from "@/components/PropertyContact";
import { CONTACTS } from "@/data/contacts";

const IMAGES = {
  hero: "/images/stock/jewellery_gold.jpg",
  store: "/images/stock/jewellery_store.jpg",
  necklace: "/images/stock/jewellery_necklace.jpg",
};

const COLLECTIONS = [
  { name: "Gold Jewellery", desc: "Traditional and contemporary designs, all 100% BIS hallmarked. From everyday pieces to wedding collections." },
  { name: "Temple Jewellery", desc: "Our specialty. Handmade temple jewellery crafted with attention to detail and deep respect for tradition." },
  { name: "Diamond Collection", desc: "Certified diamonds in elegant settings. Engagement rings, necklaces, earrings — each stone verified." },
  { name: "Bridal Sets", desc: "Complete wedding collections in gold and diamond. Designed for the biggest day and built to be passed down." },
  { name: "Custom & Handmade", desc: "Have a design in mind? Our craftsmen will bring it to life. Made-to-order jewellery for any occasion." },
  { name: "Investment Gold", desc: "Coins, bars, and certified gold. Transparent pricing, guaranteed purity, and the Amrut name behind it." },
];

const PROMISES = [
  { icon: Gem, label: "100% BIS Hallmarked" },
  { icon: Shield, label: "Certified Purity" },
  { icon: Award, label: "Certified Diamonds" },
  { icon: Sparkles, label: "Handmade Pieces" },
];

export default function AmrutJewellersPage() {
  return (
    <div data-testid="amrut-jewellers-page" className="bg-ivory">
      {/* ─── HERO ─── */}
      <section className="relative h-[80vh] md:h-screen w-full overflow-hidden">
        <img src={IMAGES.hero} alt="Amrut Jewellers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/45 to-charcoal/40 pointer-events-none" />
        <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-16 lg:p-24 pb-20 md:pb-28">
          <AnimatedSection delay={0.2}><p className="overline-text text-gold/80 mb-4">Since 1999</p></AnimatedSection>
          <AnimatedSection delay={0.4}><h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6 drop-shadow-lg">Amrut Jewellers</h1></AnimatedSection>
          <AnimatedSection delay={0.6}><p className="text-sm md:text-base text-ivory/85 max-w-lg leading-relaxed">100% hallmarked gold, certified diamonds, and handmade temple jewellery. Trusted in Karwar for over two decades.</p></AnimatedSection>
        </div>
      </section>

      {/* ─── INTRO ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <AnimatedSection>
            <div className="w-[60px] h-[1px] bg-gold/60 mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">Trusted gold,<br />honest pricing</h2>
            <p className="text-base text-charcoal/65 leading-relaxed mb-6">
              Amrut Jewellers has been in Karwar since 1999. Everything we sell is 100% BIS
              hallmarked and certified. We specialize in temple jewellery — handmade pieces
              that carry real craftsmanship, not mass production.
            </p>
            <p className="text-base text-charcoal/65 leading-relaxed">
              Our customers include local families, navy personnel, and people who travel
              to Karwar specifically for our jewellery. We do both ready-made and custom pieces.
              If you have a design, we'll make it.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="overflow-hidden h-[50vh] md:h-[60vh]">
            <img src={IMAGES.store} alt="Jewellery showroom" className="w-full h-full object-cover" />
          </AnimatedSection>
        </div>
      </section>

      {/* ─── COLLECTIONS ─── */}
      <section data-testid="jewellers-collections" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">What We Offer</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-20">Our Collections</h2></AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {COLLECTIONS.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="border-t border-charcoal/15 py-8 md:py-10">
                  <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight mb-3">{item.name}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURE NECKLACE ─── */}
      <section className="relative w-full h-[55vh] md:h-[70vh] overflow-hidden bg-charcoal">
        <img src={IMAGES.necklace} alt="Handmade temple jewellery" className="w-full h-full object-cover object-center" />
      </section>

      {/* ─── PROMISES ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">Our Guarantee</p><div className="w-[60px] h-[1px] bg-gold/50 mb-16" /></AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-10">
            {PROMISES.map((p, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="text-center"><p.icon size={28} strokeWidth={1} className="text-charcoal/45 mx-auto mb-4" /><p className="text-xs text-charcoal/65 tracking-wide">{p.label}</p></div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><PropertyContact contact={CONTACTS.jewellers} variant="light" /></AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-6">Visit the Store</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm text-charcoal/55 leading-relaxed mb-10 max-w-md mx-auto">Walk in to see the collection, or WhatsApp us for gold rates, custom orders, and availability.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href={`https://wa.me/${CONTACTS.jewellers.whatsappRaw}?text=I'd%20like%20to%20enquire%20about%20jewellery`} target="_blank" rel="noopener noreferrer" data-testid="jewellers-enquire-btn" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/25 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Enquire Now <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
